import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Intake from '../../components/skinAnalysis/Intake';
import SkinAnalysisWizard from '../../components/skinAnalysis/SkinAnalysisWizard';
import HotspotOverlay from '../../components/skinAnalysis/HotspotOverlay';
import Recommendations from '../../components/skinAnalysis/Recommendations';
import { analyze } from '../../logic/skinAnalysis/analyze';
import criteria from '../../data/skinAnalysisCriteria.json';
import { productNameForConcern } from '../../logic/skinAnalysis/recommend';
import {
  setIntake,
  setResults,
  savePlan,
  setResumeTo,
  clearResumeTo,
} from '../../redux/slice/skinAnalysisSlice';
import { activatePlan } from '../../logic/reminders/reminderScheduler';
import headerImage from '../../assets/images/MysticTan_Unity_Logo_Black.png';
import '../../components/skinAnalysis/skinAnalysis.css';

function downscaleToDataURL(canvas, maxDim = 480) {
  if (!canvas) return null;
  const scale = Math.min(1, maxDim / Math.max(canvas.width, canvas.height));
  const w = Math.round(canvas.width * scale);
  const h = Math.round(canvas.height * scale);
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  out.getContext('2d').drawImage(canvas, 0, 0, w, h);
  try {
    return out.toDataURL('image/jpeg', 0.85);
  } catch (e) {
    return null;
  }
}

const ANALYZING_MESSAGES = [
  'Reading your skin tone…',
  'Mapping your facial regions…',
  'Detecting texture & fine lines…',
  'Measuring hydration & radiance…',
  'Checking evenness & redness…',
  'Comparing against skin profiles…',
  'Matching your skin profile…',
  'Selecting products for you…',
  'Personalizing your routine…',
  'Almost there…',
  'Putting it all together…',
];
// Once the messages run out (a long first-run model download), loop the last
// few reassuring ones instead of hanging on a single message.
const MSG_TAIL = 3;
const MSG_INTERVAL_MS = 5000;

const SkinAnalysisPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((s) => s?.auth?.user);
  const intakeAnswers = useSelector((s) => s?.skinAnalysis?.intake);
  const storedResults = useSelector((s) => s?.skinAnalysis?.results);

  const [stage, setStage] = useState(storedResults ? 'results' : 'landing');
  const [analyzePct, setAnalyzePct] = useState(0);
  const [msgIndex, setMsgIndex] = useState(0);
  const [liveResults, setLiveResults] = useState(storedResults || null);
  const [activeFinding, setActiveFinding] = useState(null);
  const capturesRef = useRef(null);
  const pctRef = useRef(0);

  // Returning authed with stored results -> clear the post-auth redirect flag.
  useEffect(() => {
    if (user && storedResults) dispatch(clearResumeTo());
  }, [user, storedResults, dispatch]);

  const onIntakeComplete = (answers) => {
    dispatch(setIntake(answers));
    setStage('wizard');
  };

  const onWizardComplete = (captures) => {
    capturesRef.current = captures;
    setStage('analyzing');
  };

  useEffect(() => {
    if (stage !== 'analyzing' || !capturesRef.current) return undefined;
    let cancelled = false;
    let analysisDone = false;
    let payload = null;
    const real = { has: false, pct: 0 }; // real model-download progress, if any
    pctRef.current = 0;
    setAnalyzePct(0);
    setMsgIndex(0);

    // Messages advance on a slow timer (one every ~5s), monotonically forward.
    // When they run out, loop the last few so a long run never hangs on one.
    const msgTimer = setInterval(() => {
      setMsgIndex((i) => {
        const next = i + 1;
        if (next < ANALYZING_MESSAGES.length) return next;
        const tailStart = ANALYZING_MESSAGES.length - MSG_TAIL;
        return tailStart + ((next - tailStart) % MSG_TAIL);
      });
    }, MSG_INTERVAL_MS);

    // The bar is STRICTLY monotonic (never reverses). It eases toward 92% while
    // working, can be pulled ahead by real download progress, and completes to
    // 100% when analysis finishes.
    const tick = setInterval(() => {
      if (cancelled) return;
      const synthTarget = analysisDone ? 1 : 0.92;
      const eased = pctRef.current + (synthTarget - pctRef.current) * 0.09;
      const floor = real.has && !analysisDone ? real.pct * 0.9 : 0;
      pctRef.current = Math.min(1, Math.max(pctRef.current, eased, floor));
      setAnalyzePct(pctRef.current);
      if (analysisDone && pctRef.current > 0.985) {
        clearInterval(tick);
        clearInterval(msgTimer);
        if (!cancelled && payload) {
          setLiveResults(payload);
          dispatch(setResults(payload));
          setStage('results');
        }
      }
    }, 120);

    (async () => {
      try {
        const out = await analyze({
          captures: capturesRef.current,
          intake: intakeAnswers,
          onProgress: (p) => {
            if (p && typeof p.progress === 'number') {
              real.has = true;
              real.pct = p.progress;
            }
          },
        });
        if (cancelled) return;
        const fwd = out.forward;
        payload = {
          findings: out.findings, // each finding carries its own normalized position
          summary: out.summary,
          engine: out.engine,
          photo: downscaleToDataURL(fwd && fwd.canvas),
          createdAt: new Date().toISOString(),
        };
      } catch (err) {
        console.warn('analyze failed', err);
        payload = {
          findings: [],
          summary: 'We hit a snag analyzing — please try again.',
          engine: 'none',
          photo: null,
          createdAt: new Date().toISOString(),
        };
      } finally {
        analysisDone = true; // the tick ramps to 100% and then transitions
      }
    })();

    return () => {
      cancelled = true;
      clearInterval(tick);
      clearInterval(msgTimer);
    };
  }, [stage, intakeAnswers, dispatch]);

  const handleSignup = () => {
    dispatch(setResumeTo('/skin-analysis'));
    navigate('/accountcreate');
  };

  const handleSavePlan = async () => {
    const res = liveResults || storedResults;
    const skus = [];
    ((res && res.findings) || []).forEach((f) => (f.productSkus || []).forEach((sku) => {
      if (!skus.includes(sku)) skus.push(sku);
    }));
    const activation = await activatePlan({ productSkus: skus });
    dispatch(savePlan({ createdAt: new Date().toISOString(), productSkus: skus, scheduled: activation.scheduled || 0 }));
  };

  const res = liveResults || storedResults;
  const activeRationale = activeFinding
    ? (activeFinding.rationale || '').replace('{product}', productNameForConcern(activeFinding.concernId))
    : null;

  return (
    <div className={`skin-page skin-page--${stage}`}>
      {stage === 'landing' && (
        <div className="skin-content skin-landing">
          <img className="skin-landing__logo" src={headerImage} alt="Mystic Tan Unity" />
          <h1 className="skin-landing__title">AI Skin Analysis</h1>
          <p className="skin-landing__copy">
            Get a quick, on-device skin read and personalized product picks. Your photos stay on your device.
          </p>
          <button type="button" className="skin-btn skin-btn--primary" onClick={() => setStage('intake')}>
            Analyze my skin
          </button>
          <button type="button" className="skin-btn skin-btn--ghost" onClick={() => navigate('/fullscreenFilter')}>
            Try on a tan
          </button>
          <p className="skin-disclaimer">{criteria.disclaimer}</p>
        </div>
      )}

      {stage === 'intake' && (
        <div className="skin-content">
          <Intake onComplete={onIntakeComplete} />
        </div>
      )}

      {stage === 'wizard' && <SkinAnalysisWizard onComplete={onWizardComplete} />}

      {stage === 'analyzing' && (
        <div className="skin-content skin-analyzing">
          <div className="skin-spinner" />
          <h2 className="skin-analyzing__msg" key={msgIndex}>{ANALYZING_MESSAGES[msgIndex]}</h2>
          <div className="skin-progress"><span style={{ width: `${Math.round(analyzePct * 100)}%` }} /></div>
          <p className="skin-analyzing__note">Your photos stay on your device.</p>
        </div>
      )}

      {stage === 'results' && res && (
        <div className="skin-content skin-results">
          <HotspotOverlay
            image={res.photo}
            findings={res.findings}
            activeId={activeFinding && activeFinding.concernId}
            onSelect={setActiveFinding}
          />
          {activeRationale && <div className="skin-results__active">{activeRationale}</div>}
          <Recommendations results={res} onSignup={handleSignup} onSavePlan={handleSavePlan} />
          <button type="button" className="skin-btn skin-btn--ghost" onClick={() => { setLiveResults(null); setStage('landing'); }}>
            Start over
          </button>
        </div>
      )}
    </div>
  );
};

export default SkinAnalysisPage;
